import { FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { UserAccountPolicy } from "services/user/type"
import { userService } from "services/user/user.service"

type Props = {
  userId: string
  customHalls: string[]
  policy?: UserAccountPolicy | null
  refreshKey?: number
  onUpdate?: () => void
}

export const UserAccountPolicySection: FC<Props> = ({ userId, customHalls: initialCustomHalls, policy, refreshKey, onUpdate }) => {
  const [loading, setLoading] = useState(false)
  const [customHalls, setCustomHalls] = useState<string[]>(initialCustomHalls)
  const [localPolicy, setLocalPolicy] = useState<UserAccountPolicy | null>(policy ?? null)

  useEffect(() => {
    setCustomHalls(initialCustomHalls)
  }, [initialCustomHalls, refreshKey])

  useEffect(() => {
    setLocalPolicy(policy ?? null)
  }, [policy, refreshKey])

  const updatePolicy = useCallback(
    async (
      payload: Partial<
        Pick<
          UserAccountPolicy,
          "is_c2_streamer" | "is_dark_streamer" | "is_sv_streamer" | "disable_withdrawals" | "exclude_from_promotions"
        >
      >
    ) => {
      setLoading(true)
      try {
        const res = await userService.updateUserAccountPolicy(userId, { ...payload, sync_policy: true })
        if (res?.policy) setLocalPolicy(res.policy)
        toast.success("Updated streamer flag")
        onUpdate?.()
      } catch (error: any) {
        toast.error(error?.data?.message ?? error?.message ?? "Failed to update policy")
      } finally {
        setLoading(false)
      }
    },
    [userId, onUpdate]
  )

  const syncC2CustomHall = useCallback(
    async (nextEnabled: boolean) => {
      console.log(customHalls)
      try {
        const provider = "C2"
        const result = nextEnabled
          ? await userService.addCustomHall(userId, provider)
          : await userService.removeCustomHall(userId, provider)
        if (result?.customHalls) setCustomHalls(result.customHalls)
      } catch (error: any) {
        toast.error(error?.data?.message ?? error?.message ?? "Failed to update custom hall")
      }
    },
    [userId]
  )

  const isC2StreamerChecked = !!localPolicy?.is_c2_streamer
  const isDarkStreamerChecked = !!localPolicy?.is_dark_streamer
  const isSvStreamerChecked = !!localPolicy?.is_sv_streamer
  const isWithdrawalsDisabled = !!localPolicy?.disable_withdrawals
  const isExcludedFromPromotions = !!localPolicy?.exclude_from_promotions

  const handleToggleC2Streamer = useCallback(async () => {
    const nextEnabled = !isC2StreamerChecked
    await updatePolicy({ is_c2_streamer: nextEnabled })
    await syncC2CustomHall(nextEnabled)
  }, [isC2StreamerChecked, updatePolicy, syncC2CustomHall])

  const handleToggleDarkStreamer = useCallback(async () => {
    await updatePolicy({ is_dark_streamer: !isDarkStreamerChecked })
  }, [isDarkStreamerChecked, updatePolicy])

  const handleToggleSvStreamer = useCallback(async () => {
    await updatePolicy({ is_sv_streamer: !isSvStreamerChecked })
  }, [isSvStreamerChecked, updatePolicy])

  const handleToggleDisableWithdrawals = useCallback(async () => {
    await updatePolicy({ disable_withdrawals: !isWithdrawalsDisabled })
  }, [isWithdrawalsDisabled, updatePolicy])

  const handleToggleExcludeFromPromotions = useCallback(async () => {
    await updatePolicy({ exclude_from_promotions: !isExcludedFromPromotions })
  }, [isExcludedFromPromotions, updatePolicy])

  const checkboxes = [
    {
      id: "policy-c2-streamer",
      label: "C2 Streamer",
      checked: isC2StreamerChecked,
      onChange: handleToggleC2Streamer
    },
    {
      id: "policy-dark-streamer",
      label: "DARK Streamer",
      checked: isDarkStreamerChecked,
      onChange: handleToggleDarkStreamer
    },
    {
      id: "policy-sv-streamer",
      label: "SV Streamer",
      checked: isSvStreamerChecked,
      onChange: handleToggleSvStreamer
    },
    {
      id: "policy-disable-withdrawals",
      label: "Disable Withdrawals",
      checked: isWithdrawalsDisabled,
      onChange: handleToggleDisableWithdrawals
    },
    {
      id: "policy-exclude-promotions",
      label: "Exclude from Promotions",
      checked: isExcludedFromPromotions,
      onChange: handleToggleExcludeFromPromotions
    }
  ]

  return (
    <div className={clsx("flex flex-col gap-2", { "pointer-events-none opacity-60": loading })}>
      {checkboxes.map((checkbox) => (
        <div key={checkbox.id} className="flex items-center">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={checkbox.checked}
            onChange={checkbox.onChange}
            id={checkbox.id}
          />
          <label htmlFor={checkbox.id} className="ml-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
            {checkbox.label}
          </label>
        </div>
      ))}
    </div>
  )
}
